import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "../game/Logo";
import { LEVELS } from "../game/levels";
import { Play, BookOpen, Map as MapIcon, RotateCcw, Volume2, VolumeX, Trophy, Crown, Shirt, GitBranch, Award, Gift, LogIn, LogOut, Star, Sparkles, MonitorCog, Coins, ArrowLeft } from "lucide-react";
import { playClick, isMuted, toggleMuted } from "../game/sound";
import PlayerCountBadge from "../components/PlayerCountBadge";
import DailyCrateModal from "../components/DailyCrateModal";
import { getPlayerName, setPlayerName } from "../game/ghostService";
import { fetchTodayStats } from "../game/leaderboardService";
import { startGoogleLogin } from "../game/authService";
import { useAuth } from "../contexts/AuthContext";
import { loadQuality, saveQuality, QUALITY_OPTIONS, resolveQuality } from "../game/quality";
import GameSEOContent from "../seo/GameSEOContent";

export default function Menu({ save, onPlay, onInstructions, onLevelSelect, onLeaderboard, onVip, onCosmetics, onTalents, onAchievements, onReset }) {
  const { user, loading, signOut } = useAuth();
  const [muted, setMutedState] = useState(() => isMuted());
  const [quality, setQualityState] = useState(() => loadQuality());
  const [name, setName] = useState(() => getPlayerName());
  const [todayZombies, setTodayZombies] = useState(null);
  const [crateOpen, setCrateOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchTodayStats().then((s) => {
      if (!cancelled && s) setTodayZombies(s.zombies);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const click = (fn) => () => {
    playClick();
    fn();
  };
  const handleMute = () => {
    setMutedState(toggleMuted());
  };
  const handleQualityCycle = () => {
    playClick();
    const i = QUALITY_OPTIONS.indexOf(quality);
    const next = QUALITY_OPTIONS[(i + 1) % QUALITY_OPTIONS.length];
    saveQuality(next);
    setQualityState(next);
  };
  const qualityLabel = (() => {
    if (quality === "auto") return `Quality: Auto (${resolveQuality("auto").toUpperCase()})`;
    if (quality === "high") return "Quality: High";
    return "Quality: Low";
  })();
  const handleNameBlur = () => {
    if (name && name.trim()) setPlayerName(name.trim());
  };
  return (
    <div className="screen menu-screen" data-testid="menu-screen">
      <button
        type="button"
        onClick={handleMute}
        className={`mute-btn menu-mute ${muted ? "muted" : ""}`}
        aria-label={muted ? "Unmute" : "Mute"}
        title={muted ? "Sound off" : "Sound on"}
        data-testid="menu-mute-button"
      >
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>

      {/* Top-left quality cycler — cosmetic only, never affects gameplay. */}
      <button
        type="button"
        onClick={handleQualityCycle}
        className="menu-quality-btn"
        aria-label={qualityLabel}
        title={`Click to switch quality. ${qualityLabel}`}
        data-testid="menu-quality-button"
      >
        <MonitorCog size={13} />
        <span className="menu-quality-label" data-testid="menu-quality-label">
          {quality === "auto" ? "Auto" : quality === "high" ? "High" : "Low"}
        </span>
      </button>

      {/* Top-right account chip — Google sign-in or signed-in user */}
      <div className="menu-account" data-testid="menu-account">
        {loading ? null : user ? (
          <div className="account-chip" data-testid="account-chip-signedin">
            {user.picture ? (
              <img src={user.picture} alt="" className="account-avatar" />
            ) : (
              <div className="account-avatar account-avatar-fallback">{(user.name || "?").slice(0, 1)}</div>
            )}
            <div className="account-info">
              <div className="account-name">
                {user.name}
                {user.founder && <span className="badge-founder">FOUNDER</span>}
                {!user.founder && user.is_vip && <span className="badge-vip">VIP</span>}
              </div>
              {user.equipped_visuals?.title?.visual?.text && (
                <div
                  className="account-title"
                  style={{ color: user.equipped_visuals.title.visual.color }}
                  data-testid="account-title"
                >
                  {user.equipped_visuals.title.visual.text}
                </div>
              )}
              {user.is_vip && (
                <div
                  className={`crate-tag ${user.daily_crate_available ? "ready" : "claimed"}`}
                  data-testid={user.daily_crate_available ? "crate-tag-ready" : "crate-tag-claimed"}
                >
                  {user.daily_crate_available ? (
                    <><Gift size={10} /> Crate Ready</>
                  ) : (
                    <>Crate Claimed ✓</>
                  )}
                </div>
              )}
              <button type="button" className="account-signout" onClick={() => { playClick(); signOut(); }} data-testid="menu-signout-button">
                <LogOut size={11} /> Sign out
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="zc-btn account-signin"
            onClick={() => { playClick(); startGoogleLogin(); }}
            data-testid="menu-signin-button"
          >
            <LogIn size={16} /> Sign in with Google
          </button>
        )}
      </div>

      <div className="menu-bg">
        <div className="menu-glow menu-glow-1" />
        <div className="menu-glow menu-glow-2" />
      </div>

      <div className="menu-content">
        <Link
          to="/"
          className="menu-arcade-link"
          data-testid="menu-back-to-arcade"
          title="Back to Zombie Arcade"
        >
          <ArrowLeft size={14} /> Zombie Arcade
        </Link>
        <div className="menu-logo-area">
          <Logo size={170} />
        </div>
        <h1 className="game-title" data-testid="game-title">
          <span className="title-zombie">Zombie</span>
          <span className="title-collector">Collector</span>
          <span className="title-3d">3D</span>
        </h1>
        <p className="game-sub">Beam them up before the outbreak spreads</p>

        <div className="menu-stats">
          <div className="menu-stat" data-testid="menu-highest-wave">
            <span className="menu-stat-label">Highest Wave</span>
            <span className="menu-stat-value">{save.highestWave || 1}</span>
          </div>
          <div className="menu-stat">
            <span className="menu-stat-label">High Score</span>
            <span className="menu-stat-value">{save.totalScore}</span>
          </div>
          <div className="menu-stat">
            <span className="menu-stat-label">Worlds Cleared</span>
            <span className="menu-stat-value">{save.completedLevels.length} / {LEVELS.length}</span>
          </div>
        </div>

        {user && (
          <div className="menu-progress" data-testid="menu-progress">
            <div className="menu-progress-row">
              <div className="menu-level-pill" data-testid="menu-account-level">
                <Sparkles size={14} /> Lv {user.level || 1}
              </div>
              <div className="menu-xp-bar" data-testid="menu-xp-bar">
                <div
                  className="menu-xp-bar-fill"
                  style={{
                    width: `${Math.min(100, ((user.xp_into_level || 0) / Math.max(1, user.xp_to_next || 1)) * 100)}%`,
                  }}
                />
                <span className="menu-xp-bar-label">
                  {(user.xp_to_next || 0) > 0
                    ? `${(user.xp_into_level || 0).toLocaleString()} / ${(user.xp_to_next || 0).toLocaleString()} XP`
                    : "MAX"}
                </span>
              </div>
              {(user.talent_points || 0) > 0 && (
                <div className="menu-tp-pill" data-testid="menu-talent-points-badge">
                  <Star size={12} /> {user.talent_points} Talent Point{user.talent_points === 1 ? "" : "s"}
                </div>
              )}
              <button
                type="button"
                className="menu-coin-pill"
                data-testid="menu-coin-balance"
                onClick={click(onVip)}
                title="Click to buy more coins"
              >
                <Coins size={12} /> {(user.coins || 0).toLocaleString()}
              </button>
            </div>
            {user.stats && (
              <div className="menu-lifetime" data-testid="menu-lifetime-stats">
                <span><b>{(user.stats.total_zombies || 0).toLocaleString()}</b> lifetime zombies</span>
                <span className="dot">•</span>
                <span><b>{user.stats.total_games || 0}</b> runs</span>
                <span className="dot">•</span>
                <span>Best wave <b>{user.stats.highest_wave || 1}</b></span>
                {(user.stats.bosses_defeated || 0) > 0 && (
                  <>
                    <span className="dot">•</span>
                    <span><b>{user.stats.bosses_defeated}</b> bosses</span>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div className="menu-buttons">
          <PlayerCountBadge active={false} variant="menu" />
          {todayZombies !== null && todayZombies > 0 && (
            <div className="menu-today" data-testid="menu-today-zombies">
              {todayZombies.toLocaleString()} zombies collected today
            </div>
          )}
          <input
            type="text"
            className="menu-name-input"
            placeholder="Your name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 18))}
            onBlur={handleNameBlur}
            maxLength={18}
            data-testid="menu-name-input"
            aria-label="Your name"
          />
          <button
            type="button"
            className="zc-btn primary big"
            onClick={click(onPlay)}
            data-testid="menu-play-button"
          >
            <Play size={20} />
            Play
          </button>
          <button
            type="button"
            className="zc-btn"
            onClick={click(onLevelSelect)}
            data-testid="menu-levels-button"
          >
            <MapIcon size={18} /> Levels
          </button>
          <button
            type="button"
            className="zc-btn"
            onClick={click(onLeaderboard)}
            data-testid="menu-leaderboard-button"
          >
            <Trophy size={18} /> Leaderboard
          </button>
          <button
            type="button"
            className="zc-btn"
            onClick={click(onInstructions)}
            data-testid="menu-instructions-button"
          >
            <BookOpen size={18} /> Instructions
          </button>
          <button
            type="button"
            className="zc-btn vip-btn"
            onClick={click(onVip)}
            data-testid="menu-vip-button"
          >
            <Crown size={18} /> {user?.is_vip ? "Manage VIP" : "Become VIP"}
          </button>
          {user?.is_vip && user?.daily_crate_available && (
            <button
              type="button"
              className="zc-btn crate-btn"
              onClick={() => { playClick(); setCrateOpen(true); }}
              data-testid="menu-crate-button"
            >
              <Gift size={18} /> Open today&apos;s crate
            </button>
          )}
          {user && (
            <div className="menu-mini-row">
              <button type="button" className="zc-btn mini" onClick={click(onCosmetics)} data-testid="menu-cosmetics-button">
                <Shirt size={14} /> Cosmetics
              </button>
              <button type="button" className="zc-btn mini" onClick={click(onTalents)} data-testid="menu-talents-button">
                <GitBranch size={14} /> Talents
                {(user.talent_points || 0) > 0 && (
                  <span className="mini-pip" data-testid="menu-talents-pip">{user.talent_points}</span>
                )}
              </button>
              <button type="button" className="zc-btn mini" onClick={click(onAchievements)} data-testid="menu-achievements-button">
                <Award size={14} /> Achievements
                {(() => {
                  const ach = user?.achievements || {};
                  const claimable = Object.values(ach).filter(
                    (s) => s && s.unlocked && !s.claimed
                  ).length;
                  return claimable > 0 ? (
                    <span className="mini-pip" data-testid="menu-achievements-pip">{claimable}</span>
                  ) : null;
                })()}
              </button>
            </div>
          )}
        </div>

        {crateOpen && <DailyCrateModal onClose={() => setCrateOpen(false)} />}

        <button
          type="button"
          className="reset-link"
          onClick={() => {
            if (window.confirm("Reset all progress?")) onReset();
          }}
          data-testid="menu-reset-button"
        >
          <RotateCcw size={12} /> Reset Save
        </button>
        <GameSEOContent gameId="zombie-collector-3d" />
      </div>
    </div>
  );
}
